import SessionRecovery from "./session_recovery.jsx";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./auth_context.jsx";
import { canAccessPath, homePathFor } from "./roles.js";

/**
 * Gate any subtree behind a successful /me bootstrap.
 *
 * - While auth state is still loading (initial /me round-trip), render a
 *   minimal placeholder so we don't flash login → page → login on slow links.
 * - On unauthed, redirect to /login with `next=` pointing at the path the user
 *   was trying to reach so we can bounce them back post-login.
 * - On authed but role-restricted (a manager opening, say, /dashboard), redirect
 *   to that role's home page. See auth/roles.js.
 * - Otherwise render children.
 */
export function RequireAuth({ children }) {
  const { status, user, refresh, checkingSession } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-sm text-[color:var(--color-muted)]">
        Loading…
      </div>
    );
  }
  if (status === "unavailable") {
    return <SessionRecovery retry={refresh} checking={checkingSession} />;
  }
  if (status !== "authed") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!canAccessPath(user, location.pathname)) {
    return <Navigate to={homePathFor(user)} replace />;
  }
  return children;
}

/** Index route: each role's landing page. */
export function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={homePathFor(user)} replace />;
}
